
import { useState, useEffect, useCallback, useRef } from 'react';

type Player = 'black' | 'white';
type Cell = Player | null;
type Board = Cell[][];

const BOARD_SIZE = 8;
const DIRECTIONS = [
  [-1, -1], [-1, 0], [-1, 1],
  [0, -1],           [0, 1],
  [1, -1],  [1, 0],  [1, 1]
];

export default function Game() {
  const [board, setBoard] = useState<Board>(() => {
    const initialBoard: Board = Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(null));
    // 初始設置
    initialBoard[3][3] = 'white';
    initialBoard[3][4] = 'black';
    initialBoard[4][3] = 'black';
    initialBoard[4][4] = 'white';
    return initialBoard;
  });

  const [currentPlayer, setCurrentPlayer] = useState<Player>('black');
  const [validMoves, setValidMoves] = useState<boolean[][]>(() => 
    Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(false))
  );
  const [scores, setScores] = useState({ black: 2, white: 2 });
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState<Player | 'tie' | null>(null);
  const [showCustomization, setShowCustomization] = useState(false);
  const [blackImage, setBlackImage] = useState<string>('https://static.readdy.ai/image/c2784296c91e4fdfa438138a6e6ce7cc/414798828f6c6bf36cd80669d3360803.png');
  const [whiteImage, setWhiteImage] = useState<string>('https://static.readdy.ai/image/c2784296c91e4fdfa438138a6e6ce7cc/c8281bc841cc445ac86f41290c68652a.png');
  const [audioContextInitialized, setAudioContextInitialized] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);

  // 初始化音效上下文
  const initializeAudioContext = useCallback(() => {
    if (!audioContextInitialized) {
      try {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        
        // 解鎖音效播放 - 創建無聲音效
        if (audioContextRef.current?.state === 'suspended') {
          const oscillator = audioContextRef.current.createOscillator();
          const gainNode = audioContextRef.current.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContextRef.current.destination);
          
          gainNode.gain.setValueAtTime(0, audioContextRef.current.currentTime);
          oscillator.frequency.setValueAtTime(440, audioContextRef.current.currentTime);
          
          oscillator.start(audioContextRef.current.currentTime);
          oscillator.stop(audioContextRef.current.currentTime + 0.001);
          
          audioContextRef.current.resume().then(() => {
            setAudioContextInitialized(true);
          });
        } else {
          setAudioContextInitialized(true);
        }
      } catch (error) {
        console.warn('無法初始化音效:', error);
      }
    }
  }, [audioContextInitialized]);

  // 音效播放函數 - 優化手機兼容性
  const playSound = useCallback((type: 'black' | 'white' | 'flip' | 'win') => {
    if (!audioContextRef.current || !audioContextInitialized) {
      return;
    }

    try {
      let frequency: number;
      let duration: number;
      
      switch (type) {
        case 'black':
          frequency = 440;
          duration = 0.15;
          break;
        case 'white':
          frequency = 500;
          duration = 0.15;
          break;
        case 'flip':
          frequency = 400;
          duration = 0.1;
          break;
        case 'win':
          const frequencies = [523, 659, 784, 1047];
          frequencies.forEach((freq, index) => {
            setTimeout(() => {
              if (audioContextRef.current && audioContextInitialized) {
                try {
                  const oscillator = audioContextRef.current.createOscillator();
                  const gainNode = audioContextRef.current.createGain();
                  
                  oscillator.connect(gainNode);
                  gainNode.connect(audioContextRef.current.destination);
                  
                  oscillator.frequency.setValueAtTime(freq, audioContextRef.current.currentTime);
                  oscillator.type = 'sine';
                  
                  gainNode.gain.setValueAtTime(0, audioContextRef.current.currentTime);
                  gainNode.gain.linearRampToValueAtTime(0.2, audioContextRef.current.currentTime + 0.01);
                  gainNode.gain.exponentialRampToValueAtTime(0.001, audioContextRef.current.currentTime + 0.3);
                  
                  oscillator.start(audioContextRef.current.currentTime);
                  oscillator.stop(audioContextRef.current.currentTime + 0.3);
                } catch (e) {
                  console.warn('音效播放失敗:', e);
                }
              }
            }, index * 150);
          });
          return;
        default:
          return;
      }
      
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      
      // Fixed typo: correctly connect oscillator to gain node
      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      
      oscillator.frequency.setValueAtTime(frequency, audioContextRef.current.currentTime);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0, audioContextRef.current.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.2, audioContextRef.current.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContextRef.current.currentTime + duration);
      
      oscillator.start(audioContextRef.current.currentTime);
      oscillator.stop(audioContextRef.current.currentTime + duration);
    } catch (error) {
      console.warn('音效播放失敗:', error);
    }
  }, [audioContextInitialized]);

  // 檢查方向上是否可以翻轉
  const canFlipInDirection = useCallback((board: Board, row: number, col: number, direction: number[], player: Player): boolean => {
    const [dx, dy] = direction;
    let x = row + dx;
    let y = col + dy;
    let hasOpponent = false;

    while (x >= 0 && x < BOARD_SIZE && y >= 0 && y < BOARD_SIZE) {
      if (board[x][y] === null) return false;
      if (board[x][y] === player) return hasOpponent;
      hasOpponent = true;
      x += dx;
      y += dy;
    }
    return false;
  }, []);

  // 檢查是否為有效移動
  const isValidMove = useCallback((board: Board, row: number, col: number, player: Player): boolean => {
    if (board[row][col] !== null) return false;
    return DIRECTIONS.some(direction => canFlipInDirection(board, row, col, direction, player));
  }, [canFlipInDirection]);

  // 獲取所有有效移動
  const getValidMoves = useCallback((board: Board, player: Player): boolean[][] => {
    const moves: boolean[][] = Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(false));
    for (let row = 0; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        moves[row][col] = isValidMove(board, row, col, player);
      }
    }
    return moves;
  }, [isValidMove]);

  // 執行翻轉
  const flipInDirection = (newBoard: Board, row: number, col: number, direction: number[], player: Player) => {
    const [dx, dy] = direction;
    let x = row + dx;
    let y = col + dy;

    while (x >= 0 && x < BOARD_SIZE && y >= 0 && y < BOARD_SIZE && newBoard[x][y] !== player) {
      newBoard[x][y] = player;
      x += dx;
      y += dy;
    }
  };

  // 執行移動
  const makeMove = useCallback((row: number, col: number) => {
    if (!validMoves[row][col] || gameOver) return;

    // 在用戶點擊時初始化音效
    initializeAudioContext();

    const newBoard = board.map(row => [...row]);
    newBoard[row][col] = currentPlayer;

    // 立即播放下棋音效
    setTimeout(() => playSound(currentPlayer), 0);

    let flippedCount = 0;
    DIRECTIONS.forEach(direction => {
      if (canFlipInDirection(board, row, col, direction, currentPlayer)) {
        flipInDirection(newBoard, row, col, direction, currentPlayer);
        
        const [dx, dy] = direction;
        let x = row + dx;
        let y = col + dy;
        while (x >= 0 && x < BOARD_SIZE && y >= 0 && y < BOARD_SIZE && board[x][y] !== currentPlayer && board[x][y] !== null) {
          flippedCount++;
          x += dx;
          y += dy;
        }
      }
    });

    if (flippedCount > 0) {
      setTimeout(() => playSound('flip'), 200);
    }

    setBoard(newBoard);
    setCurrentPlayer(currentPlayer === 'black' ? 'white' : 'black');
  }, [board, currentPlayer, validMoves, gameOver, canFlipInDirection, playSound, initializeAudioContext]);

  // 計算分數
  const calculateScores = useCallback((board: Board) => {
    let blackCount = 0;
    let whiteCount = 0;
    board.forEach(row => {
      row.forEach(cell => {
        if (cell === 'black') blackCount++;
        if (cell === 'white') whiteCount++;
      });
    });
    return { black: blackCount, white: whiteCount };
  }, []);

  // 重置遊戲
  const resetGame = () => {
    // 在重置時初始化音效
    initializeAudioContext();
    
    const initialBoard: Board = Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(null));
    initialBoard[3][3] = 'white';
    initialBoard[3][4] = 'black';
    initialBoard[4][3] = 'black';
    initialBoard[4][4] = 'white';
    setBoard(initialBoard);
    setCurrentPlayer('black');
    setGameOver(false);
    setWinner(null);
  };

  // 更新遊戲狀態
  useEffect(() => {
    const newScores = calculateScores(board);
    setScores(newScores);

    const currentValidMoves = getValidMoves(board, currentPlayer);
    const opponentValidMoves = getValidMoves(board, currentPlayer === 'black' ? 'white' : 'black');
    
    setValidMoves(currentValidMoves);

    const hasCurrentMoves = currentValidMoves.some(row => row.some(cell => cell));
    const hasOpponentMoves = opponentValidMoves.some(row => row.some(cell => cell));

    if (!hasCurrentMoves && !hasOpponentMoves) {
      setGameOver(true);
      if (newScores.black > newScores.white) setWinner('black');
      else if (newScores.white > newScores.black) setWinner('white');
      else setWinner('tie');
      
      setTimeout(() => playSound('win'), 500);
    } else if (!hasCurrentMoves) {
      setCurrentPlayer(currentPlayer === 'black' ? 'white' : 'black');
    }
  }, [board, currentPlayer, calculateScores, getValidMoves, playSound]);

  // 處理圖片上傳
  const handleImageUpload = (player: Player, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        if (player === 'black') {
          setBlackImage(result);
        } else {
          setWhiteImage(result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // 處理自定義按鈕點擊
  const handleCustomizationClick = () => {
    initializeAudioContext();
    setShowCustomization(!showCustomization);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative">
      {/* 簡潔背景裝飾 */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-40 h-40 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-blue-300 rounded-full blur-2xl"></div>
      </div>

      <div className="relative z-10 p-6">
        {/* Logo 和標題 - 縮小logo 30% */}
        <div className="text-center mb-6">
          <div className="mb-4">
            <img 
              src="https://static.readdy.ai/image/c2784296c91e4fdfa438138a6e6ce7cc/f344ca78a68eaf6e8dad87f801573847.png" 
              alt="Noir Blanc Logo" 
              className="w-14 h-14 mx-auto rounded-full shadow-xl border-2 border-white/30"
            />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Pacifico, serif' }}>
            惡魔與天使
          </h1>
          <p className="text-xl text-blue-200 font-medium">黑黑白白</p>
        </div>

        {/* 分數顯示 */}
        <div className="flex justify-between items-center mb-6 bg-white/10 backdrop-blur-sm rounded-2xl p-4 max-w-sm mx-auto">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-full bg-gray-300 border-2 border-red-400 shadow-lg flex items-center justify-center">
              {blackImage ? (
                <img src={blackImage} alt="黑子" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <i className="ri-skull-2-fill text-red-400 text-xl"></i>
              )}
            </div>
            <div className="text-center">
              <div className="text-white/80 text-sm">惡魔</div>
              <div className="text-2xl font-bold text-red-400">{scores.black}</div>
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-white/90 text-sm mb-2">當前回合</div>
            <div className={`w-8 h-8 rounded-full mx-auto border-2 ${currentPlayer === 'black' ? 'bg-gray-300 border-red-400' : 'bg-gray-300 border-blue-400'}`}>
              {currentPlayer === 'black' && blackImage ? (
                <img src={blackImage} alt="黑子" className="w-full h-full rounded-full object-cover" />
              ) : currentPlayer === 'white' && whiteImage ? (
                <img src={whiteImage} alt="白子" className="w-full h-full rounded-full object-cover" />
              ) : (
                <i className={`${currentPlayer === 'black' ? 'ri-skull-2-fill text-red-400' : 'ri-heart-fill text-blue-400'} text-sm flex items-center justify-center h-full`}></i>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <div className="text-center">
              <div className="text-white/80 text-sm">天使</div>
              <div className="text-2xl font-bold text-blue-400">{scores.white}</div>
            </div>
            <div className="w-12 h-12 rounded-full bg-gray-300 border-2 border-blue-400 shadow-lg flex items-center justify-center">
              {whiteImage ? (
                <img src={whiteImage} alt="白子" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <i className="ri-heart-fill text-blue-500 text-xl"></i>
              )}
            </div>
          </div>
        </div>

        {/* 遊戲棋盤 - 進一步放大 */}
        <div className="w-full px-2 mb-6">
          <div className="w-full max-w-lg mx-auto">
            <div className="grid grid-cols-8 gap-0.5 p-1">
              {board.map((row, rowIndex) =>
                row.map((cell, colIndex) => (
                  <button
                    key={`${rowIndex}-${colIndex}`}
                    className={`aspect-square bg-gradient-to-br from-slate-400 to-slate-500 border-0 rounded-sm flex items-center justify-center relative transition-colors duration-200 hover:bg-gradient-to-br hover:from-slate-300 hover:to-slate-400 ${
                      validMoves[rowIndex][colIndex] ? 'ring-1 ring-green-400 bg-gradient-to-br from-green-700 to-emerald-800' : ''
                    }`}
                    onClick={() => makeMove(rowIndex, colIndex)}
                    disabled={gameOver}
                  >
                    {cell && (
                      <div className={`w-5/6 h-5/6 rounded-full transition-all duration-300 transform hover:scale-110 shadow-lg ${
                        cell === 'black' 
                          ? 'bg-gray-300 border border-red-500 shadow-red-500/30' 
                          : 'bg-gray-300 border border-blue-500 shadow-blue-500/30'
                      }`}>
                        {cell === 'black' && blackImage ? (
                          <img src={blackImage} alt="黑子" className="w-full h-full rounded-full object-cover" />
                        ) : cell === 'white' && whiteImage ? (
                          <img src={whiteImage} alt="白子" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <i className={`${cell === 'black' ? 'ri-skull-2-fill text-red-400' : 'ri-heart-fill text-blue-500'} text-lg flex items-center justify-center h-full`}></i>
                        )}
                      </div>
                    )}
                    {validMoves[rowIndex][colIndex] && !cell && (
                      <div className="w-4 h-4 bg-green-400 rounded-full opacity-80 animate-pulse"></div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* 控制按鈕 */}
        <div className="flex flex-col space-y-4 max-w-sm mx-auto">
          <button
            onClick={resetGame}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-4 px-6 rounded-2xl font-bold text-lg shadow-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105"
          >
            <i className="ri-refresh-line mr-2"></i>
            重新開始
          </button>
          
          <button
            onClick={handleCustomizationClick}
            className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-4 px-6 rounded-2xl font-bold text-lg shadow-xl hover:from-blue-700 hover:to-cyan-700 transition-all duration-200 transform hover:scale-105"
          >
            <i className="ri-image-line mr-2"></i>
            自訂圖片
          </button>
        </div>

        {/* 自訂圖片面板 */}
        {showCustomization && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-800">自訂圖片</h3>
                <button
                  onClick={() => setShowCustomization(false)}
                  className="text-gray-500 hover:text-gray-700 bg-gray-100 rounded-full p-2 hover:bg-gray-200 transition-all duration-200"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="bg-red-50 p-4 rounded-2xl border border-red-200">
                  <label className="block text-sm font-bold text-red-700 mb-3">
                    <i className="ri-skull-2-line mr-2"></i>
                    惡魔（黑子）圖片
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('black', e)}
                    className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-red-500 file:text-white hover:file:bg-red-600 file:transition-all file:duration-200"
                  />
                  {blackImage && (
                    <div className="mt-3 flex items-center space-x-3 bg-white p-3 rounded-xl">
                      <img src={blackImage} alt="黑子預覽" className="w-10 h-10 rounded-full object-cover border-2 border-red-300" />
                      <button
                        onClick={() => setBlackImage('')}
                        className="text-red-500 hover:text-red-7
00 bg-red-50 rounded-lg px-3 py-1 hover:bg-red-100 transition-all duration-200 text-sm font-semibold"
                      >
                        移除
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="bg-blue-50 p-4 rounded-2xl border border-blue-200">
                  <label className="block text-sm font-bold text-blue-700 mb-3">
                    <i className="ri-heart-line mr-2"></i>
                    天使（白子）圖片
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('white', e)}
                    className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600 file:transition-all file:duration-200"
                  />
                  {whiteImage && (
                    <div className="mt-3 flex items-center space-x-3 bg-white p-3 rounded-xl">
                      <img src={whiteImage} alt="白子預覽" className="w-10 h-10 rounded-full object-cover border-2 border-blue-300" />
                      <button
                        onClick={() => setWhiteImage('')}
                        className="text-blue-500 hover:text-blue-700 bg-blue-50 rounded-lg px-3 py-1 hover:bg-blue-100 transition-all duration-200 text-sm font-semibold"
                      >
                        移除
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 遊戲結束彈窗 */}
        {gameOver && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl p-10 text-center max-w-sm w-full shadow-2xl">
              <div className="mb-6 flex justify-center">
                {winner === 'black' ? (
                  <div className="w-20 h-20 rounded-full bg-gray-300 border-4 border-red-500 shadow-xl flex items-center justify-center">
                    {blackImage ? (
                      <img src={blackImage} alt="惡魔勝出" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <i className="ri-skull-2-fill text-red-500 text-4xl"></i>
                    )}
                  </div>
                ) : winner === 'white' ? (
                  <div className="w-20 h-20 rounded-full bg-gray-300 border-4 border-blue-500 shadow-xl flex items-center justify-center">
                    {whiteImage ? (
                      <img src={whiteImage} alt="天使勝出" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <i className="ri-heart-fill text-blue-500 text-4xl"></i>
                    )}
                  </div>
                ) : (
                  <div className="text-8xl">🤝</div>
                )}
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                {winner === 'tie' ? '平局！' : winner === 'black' ? '惡魔獲勝！' : '天使獲勝！'}
              </h2>
              <div className="bg-gray-100 p-4 rounded-2xl mb-6">
                <p className="text-gray-700 font-semibold text-lg">
                  最終比分
                </p>
                <p className="text-2xl font-bold text-gray-800">
                  惡魔 {scores.black} : {scores.white} 天使
                </p>
              </div>
              <button
                onClick={resetGame}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-4 px-10 rounded-2xl font-bold text-xl shadow-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 w-full"
              >
                <i className="ri-play-fill mr-3"></i>
                再玩一局
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
